import objectHash from "object-hash";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { debounceTime, Subject } from "rxjs";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  CancellationToken,
  DimensionField,
  PivotColumnResponse,
  PivotDataResponse,
  QueryLoggingItem,
  ReportField,
  RunPivotReportRequest,
  SortField,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { getAllFilters } from "../../../../utilities/getAllConditions.ts";
import { isPivotConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import type { PivotGridState } from "./gridState.types.ts";
import useDataLoadingViewer from "./useDataLoadingViewer.ts";

interface Props {
  rows: DimensionField[];
  columns: DimensionField[];
  values: ReportField[];
  sorts: SortField[];
  requestConfig: RunPivotReportRequest;
  runReport: (
    dataSetId: string,
    body: RunPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void;
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void;
}
export default function useGridStateViewer({
  rows,
  columns,
  values,
  sorts,
  requestConfig,
  runReport,
  updateDevTools,
  setEtag,
}: Props): PivotGridState & { initialized: boolean } {
  const { dimensions, refreshToken, conditions } = useBiCommonDataContext();
  const subjectDelay = useMemo(() => new Subject<string>(), []);

  const [pivotColumns, setPivotColumns] = useState<PivotColumnResponse[]>([]);
  const { calculate, error, calculating, pivot, failedMeasures, initialized } = useDataLoadingViewer({
    requestConfig,
    runReport,
    updateDevTools,
    setEtag,
  });

  const conditionsFiltersHash = useMemo(() => objectHash(getAllFilters(conditions, values)), [conditions, values]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(100)).subscribe(calculate);
    return () => subscription.unsubscribe();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const configurationValidity = useMemo(() => {
    const dimensionFields = rows.concat(columns).concat(rows);
    return isPivotConfigurationValid(conditions, dimensionFields, values, dimensions);
  }, [columns, conditions, dimensions, rows, values]);

  useEffect(() => {
    if (!configurationValidity.valid) {
      return;
    }
    subjectDelay.next(Math.random().toString());
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conditionsFiltersHash, refreshToken, configurationValidity.valid, sorts]);

  useEffect(() => {
    if (!pivot) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setPivotColumns([]);
    } else {
      const gridColumns = pivot.columns;
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setPivotColumns(gridColumns);
    }
  }, [pivot]);

  return {
    pivot,
    columns: pivotColumns,
    error,
    calculating,
    configurationValidity,
    failedMeasures,
    initialized,
  };
}
